import type { Square } from "chess.js";
import type { PgnBoardShape } from "~/utils/pgnBoardShapes";
import type {
  ResultPositionsRow,
  ResultPositionsSubmittedErrorKind,
  ResultPositionsTargetKey,
} from "~/lib/resultPositions";
import type { TimeControlSettings } from "~/domain/timeControl/timeControl";
import type { TimeControlIncorrectMove } from "~/domain/timeControl/timeControlMoveTiming";
import type { GameAnalysisMetadata, GameAnalysisResult } from "~/features/gameAnalysis/metadata";
export type { GameAnalysisMetadata } from "~/features/gameAnalysis/metadata";

export enum UserRoleEnum {
  STUDENT = "student",
  TRAINER = "trainer",
  ADMIN = "admin",
}

/* eslint-disable */
export function IsValidUserRole(role: any): role is UserRoleEnum {
  return Object.values(UserRoleEnum).includes(role);
}
/* eslint-enable */

/**
 * Represents a user of the application.
 * Maps to the 'Users' table.
 */
export interface User {
  pgnPlayerNames: string[];
  userId: number;
  username: string; // UNIQUE NOT NULL
  role: UserRoleEnum; // TEXT NOT NULL CHECK (role IN ('student', 'trainer', 'admin'))
  rating: number;
  lifeRating: number;
  createdAt: string; // TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP (ISO8601 format recommended)
}

export enum ProblemSetOrientationEnum {
  DEFAULT = "default",
  WHITE = "white",
  BLACK = "black",
}

export enum ProblemSetSolveModeEnum {
  DEFAULT = "default",
  PLAN = "plan",
}

export enum ProblemSetVisibilityEnum {
  PUBLIC = "public",
  PRIVATE = "private",
  HIDDEN = "hidden",
}

export enum ProblemSetTypeEnum {
  DEFAULT = "default",
  OPENING = "opening",
  GAME_ANALYSIS = "game-analysis",
}

export enum OpeningProblemVisibilityEnum {
  PUBLIC = "public",
  PRIVATE = "private",
  HIDDEN = "hidden",
}

export enum RatingOutcomeAlgorithmEnum {
  DEFAULT = "default",
  RATING_BY_MOVES_COUNT = "rating_by_moves_count",
}

export type BoardQuestionType = "color" | "diagonal";
export type BoardQuestion =
  | {
      kind: "board-color";
      target: string;
    }
  | {
      kind: "board-diagonal";
      target: string;
      shape: 1 | 2 | 4;
    };

export type GeneralQuestion = {
  kind: "general";
  text: string;
  options: Array<{ index: number; label: string }>;
  hasHint?: boolean;
};

export type ResultPositionsQuestion = {
  kind: "result-positions";
  target: string;
  rows: ResultPositionsRow[];
};

export type OpeningProblemContext = {
  openingProblemId: number;
  moves: string[];
};

/**
 * Represents a collection of chess problems (a study), typically loaded from a PGN file.
 * Contains default settings derived from the PGN's service game.
 * Maps to the 'ProblemSets' table.
 */
export interface ProblemSet {
  problemSetId: number;
  name: string; // TEXT NOT NULL
  pgnFileSource: string | null; // TEXT, Path or identifier of the original PGN file
  uploadedByUserId: number | null; // INTEGER, FK to Users (NULL allowed)
  visibility: ProblemSetVisibilityEnum;
  type: ProblemSetTypeEnum;
  openingGroupOrder: number | null;
  uploadedAt: string; // TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP (ISO8601)

  // Default settings from the service game's macros
  defaultTypeName: string | null; // TEXT
  defaultDescription: string | null; // TEXT
  defaultScore: number; // INTEGER DEFAULT 0
  defaultPremoves: number; // INTEGER DEFAULT 0
  defaultOrientation: ProblemSetOrientationEnum; // TEXT DEFAULT 'default' CHECK
  defaultSolveMode: ProblemSetSolveModeEnum; // TEXT DEFAULT 'default' CHECK
  defaultAllowHint: boolean; // INTEGER DEFAULT 1 (Mapped to boolean)
  defaultHintText: string | null; // TEXT
  defaultHintDelay: number; // INTEGER DEFAULT 0 (Seconds)
  defaultRetryCount: number; // INTEGER DEFAULT 0
  defaultRetryDelay: number; // INTEGER DEFAULT 0 (Seconds)
  defaultVariantsRequired: boolean; // INTEGER DEFAULT 0 (Mapped to boolean)
  defaultAutoHideTimeoutSec: number | null; // INTEGER
}

/**
 * Represents an individual chess problem/task within a ProblemSet.
 * Maps to the 'Problems' table.
 */
export interface Problem {
  problemId: number;
  problemSetId: number; // INTEGER NOT NULL, FK to ProblemSets
  problemOrder: number; // INTEGER NOT NULL (Order within the PGN)
  name: string | null; // TEXT (From [Round] tag)
  fen: string; // TEXT NOT NULL (From [FEN] tag)
  showFen: string | null;
  setupTag: boolean; // INTEGER NOT NULL DEFAULT 0 (Mapped to boolean, usually true if FEN present)
  solutionMoves: string; // TEXT NOT NULL (PGN move text with variants, comments, macros)
  gameAnalysisMetadata?: GameAnalysisMetadata | null;

  // Specific settings overriding defaults from ProblemSet (null means use default)
  description: string | null; // TEXT
  score: number | null; // INTEGER
  premoves: number | null; // INTEGER
  premovesSAN: string | null; // TEXT (SAN notation for premoves, if applicable)
  orientation: "default" | "white" | "black" | null; // TEXT CHECK
  turn: "w" | "b" | null; // TEXT CHECK (Current turn, derived from FEN)
  solveMode: "default" | "plan" | null; // TEXT CHECK
  allowHint: boolean | null; // INTEGER CHECK (Mapped to boolean)
  hintText: string | null; // TEXT
  hintDelay: number | null; // INTEGER
  softHint: boolean | null;
  showMoveComments: boolean | null;
  headComment: string | null;
  retryCount: number | null; // INTEGER
  retryDelay: number | null; // INTEGER
  variantsRequired: boolean | null; // INTEGER CHECK (Mapped to boolean)
  strictMoves: number | null;
  rating: number | null;
  ratingAlgorithm?: RatingOutcomeAlgorithmEnum;
  allowAskMoves: boolean | null; // INTEGER CHECK (Mapped to boolean)
  handMove: boolean | null;
  requestQuestion: "0" | "1" | "1/2" | null;
  answer: string | null; // TEXT (Answer to the question, if applicable)
  boardQuestionType: BoardQuestionType | null;
  boardQuestionTarget: string | null;
  questionText: string | null;
  questionOptionsJson: string | null;
  questionCorrectOptionIndex: number | null;
  questionHintText: string | null;
  questionExplanationText: string | null;
  resultPositionsTarget: string | null;
  investigationTargetFen: string | null;
  viewSolution: boolean;
  allowVariants: boolean | null;
  equivalentBestMoves: boolean | null;
  minCheckMoves: number | null; // INTEGER (Minimum number of checks required)
  noRating: boolean | null;
  autoHideTimeoutSec: number | null;
  timeControl: TimeControlSettings | null;
  hideBoard: boolean | null;
  textPosition: boolean | null;
  showResult: boolean | null;
  previousProblemId: number | null;
  previousMoveSan: string | null;
  previousMoveFen: string | null;
  openingContextProblemId: number | null;
  openingContextMoves: string | null;
  openingVisibility: OpeningProblemVisibilityEnum | null;
  openingForAnalysis: boolean;
  openingUploadedAt: string | null;
  openingUploadedByUserId: number | null;
  openingVersion: number | null;
  openingTotalMoves: number | null;
  openingForkCount: number | null;
  openingTerminalPositionCount: number | null;
}

export type OpeningMetrics = {
  totalMoves: number;
  forkCount: number;
  terminalPositionCount: number;
};

export type OpeningListItem = {
  problemSetId: number;
  problemId: number;
  title: string;
  orientation: "white" | "black" | null;
  openingMetrics: OpeningMetrics | null;
};

export type AdminOpeningListItem = OpeningListItem & {
  openingForAnalysis: boolean;
  openingVersion: number;
  openingVisibility: OpeningProblemVisibilityEnum | null;
  openingUploadedAt: string | null;
  openingUploadedByUserId: number | null;
  openingUploadedByUsername: string | null;
};

export enum TrainingSessionStatusEnum {
  IN_PROGRESS = "in_progress",
  COMPLETED = "completed",
  CHECKING = "checking",
  ARCHIVED = "archived",
}

/**
 * Represents a user's attempt to solve a specific ProblemSet.
 * Maps to the 'TrainingSessions' table.
 */
export interface TrainingSession {
  sessionId: number;
  userId: number | null; // INTEGER, FK to Users (NULL for anonymous)
  problemSetId: number; // INTEGER NOT NULL, FK to ProblemSets
  status: TrainingSessionStatusEnum; // TEXT NOT NULL CHECK DEFAULT 'in_progress'
  startedAt: string; // TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP (ISO8601)
  lastUpdatedAt: string; // TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP (ISO8601)
  completedAt: string | null; // TEXT (ISO8601)
  checkedAt: string | null; // TEXT (ISO8601)

  // Optional: Summary fields (could be calculated dynamically)
  // totalScore?: number;
  // totalMaxScore?: number;
  // totalDurationSec?: number;
}

export enum SessionTaskAttemptResultEnum {
  PENDING = "pending",
  WIN = "1-0",
  DRAW = "1/2-1/2",
  LOSS = "0-1",
}

export enum SessionTaskAttemptStateEnum {
  NOT_STARTED = "not_started",
  IN_PROGRESS = "in_progress",
  HAS_MISTAKES = "has_mistakes",
  DONE = "done",
  CANCELLED = "cancelled",
}

export type MoveNagEntry = {
  index: number;
  nags: string[];
};

export type MoveBoardShapeEntry = {
  index: number;
  shapes: PgnBoardShape[];
};

export type MoveAnnotationPayload = {
  moveNags?: MoveNagEntry[];
  moveBoardShapes?: MoveBoardShapeEntry[];
};

/**
 * Represents the state and result of a user attempting a specific Problem
 * within a specific TrainingSession.
 * Maps to the 'SessionTaskAttempts' table.
 */
export interface SessionTaskAttempt extends MoveAnnotationPayload {
  attemptId: number;
  sessionId: number; // INTEGER NOT NULL, FK to TrainingSessions
  problemId: number; // INTEGER NOT NULL, FK to Problems
  mainAttemptId: number | null;
  state: SessionTaskAttemptStateEnum; // TEXT NOT NULL CHECK DEFAULT 'not_started'
  result: SessionTaskAttemptResultEnum; // TEXT CHECK DEFAULT 'pending'
  finalState: SessionTaskAttemptStateEnum; // TEXT NOT NULL CHECK DEFAULT 'not_started'
  finalResult: SessionTaskAttemptResultEnum; // TEXT CHECK DEFAULT 'pending'
  hintUsed: boolean; // INTEGER NOT NULL DEFAULT 0 (Mapped to boolean)
  externalHint: boolean; // INTEGER NOT NULL DEFAULT 0 (Mapped to boolean)
  internalHint: boolean; // INTEGER NOT NULL DEFAULT 0 (Mapped to boolean)
  finalExternalHint: boolean; // INTEGER NOT NULL DEFAULT 0 (Mapped to boolean)
  finalInternalHint: boolean; // INTEGER NOT NULL DEFAULT 0 (Mapped to boolean)
  optionalHintUsed: number | null; // INTEGER DEFAULT 0 (Bit set of how each optional hint was used)
  retryCountUsed: number; // INTEGER NOT NULL DEFAULT 0
  durationSec: number; // INTEGER NOT NULL DEFAULT 0 (Accumulated time)
  score: number; // REAL DEFAULT 0.0 (Achieved score)
  maxScore: number; // INTEGER NOT NULL DEFAULT 0 (Max possible score for this task)
  userMoves: string | null; // TEXT (User's entered moves, PGN-like or JSON)
  pointsChange: number; // INTEGER DEFAULT 0 (Change in points for this attempt)
  taskRating: number; // INTEGER DEFAULT 0 (Rating of the task, if applicable)
  strictMoves: number | null;
  questionResult: SessionTaskAttemptResultEnum | null; // TEXT (Result of the question, if applicable)
  lockedMoves: number | null; // INTEGER DEFAULT 0 (Number of moves locked in the task, if applicable)
  solutionMoves: string | null;
  premoves: number;
  premovesSAN: string | null;
  revealed: boolean;
  equivalentMove: string | null; // TEXT (If this variation is equivalent to another, store the move index in format <move index><color>, e.g. `1w`)
  skippedEquivalent: boolean; // INTEGER NOT NULL DEFAULT 0 (If this variation was skipped due to equivalence)
  solveOrder: number;
  startedAt: string | null; // TEXT (ISO8601, when task was first opened)
  autoHideStartedAt: string | null; // TEXT (ISO8601, when AutoHide "Start" was first pressed)
  timeControlStartedAt: string | null; // TEXT (ISO8601, when all-task TimeControl started)
  timeControlMoveStartedAt: string | null; // TEXT (ISO8601, when current-move TimeControl started)
  timeControlExpiredAt: string | null; // TEXT (ISO8601, when TimeControl expired)
  lastUpdatedAt: string | null; // TEXT (ISO8601, timestamp of last interaction)
  incorrectSolutions?: IncorrectSolutionsByPlyIndex;
  timeControlIncorrectMoves?: TimeControlIncorrectMove[];
  timeControlElapsedSec?: number;
  moveNags?: MoveNagEntry[];
}

// Interface for the return type when fetching session with attempts
export interface TrainingSessionWithAttempts extends TrainingSession {
  attempts: SessionTaskAttempt[];
  variations?: SessionTaskAttempt[];
}

/**
 * Represents a detailed log entry for an event occurring during a SessionTaskAttempt.
 * Maps to the 'LogEntries' table.
 */
export interface LogEntry {
  logEntryId: number;
  attemptId: number; // INTEGER NOT NULL, FK to SessionTaskAttempts
  eventType: // TEXT NOT NULL CHECK
  | "start"
    | "show_hint"
    | "submit_attempt"
    | "save"
    | "check_result"
    | "system_question"
    | "submit_answer"
    | "cancel"
    | "end";
  timestamp: string; // TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP (ISO8601)
  details: string | null; // TEXT (Event-specific data, potentially JSON string)
}

export type LogEventType =
  | "start"
  | "show_hint"
  | "submit_attempt"
  | "save"
  | "check_result"
  | "system_question"
  | "submit_answer"
  | "cancel"
  | "end";

/**
 * Represents an incorrect solution submitted by a user during problem solving.
 * Maps to the 'IncorrectSolutions' table.
 */
export interface IncorrectSolution {
  incorrectSolutionId: number;
  userId: number | null;
  problemId: number;
  attemptId: number;
  createdAt: string; // TEXT NOT NULL DEFAULT CURRENT_TIMESTAMP (ISO8601)
  plyIndex: number; // 0-based index of the move where the error occurred
  solution: string; // JSON string of moves from error position to end
  retryCountUsed: number;
}

export interface IncorrectResultPosition {
  incorrectResultPositionId: number;
  userId: number | null;
  problemId: number;
  attemptId: number;
  createdAt: string;
  resultPositionKey: ResultPositionsTargetKey;
  submittedSquare: string;
  errorKind: ResultPositionsSubmittedErrorKind;
  retryCountUsed: number;
}

/**
 * Incorrect solutions grouped by plyIndex (0-based move index where error occurred).
 * Key is the plyIndex as a number, value is an array of solution strings (JSON arrays of moves).
 */
export type IncorrectSolutionsByPlyIndex = Record<number, string[]>;

export type ProblemDetails = {
  problemId: number;
  problemSetId?: number;
  problemSetType?: ProblemSetTypeEnum;
  name?: string;
  fen: string;
  showFen?: string;
  description?: string;
  premovesSAN?: string[];
  turn: "w" | "b";
  orientation?: "default" | "white" | "black";
  strictMoves: number;
  rating: number;
  allowAskMoves?: boolean;
  handMove?: boolean;
  needAnswer?: boolean;
  noRating?: boolean;
  autoHideTimeoutSec?: number;
  timeControl?: TimeControlSettings;
  hideBoard?: boolean;
  textPosition?: boolean;
  hint?: string;
  hasHint?: boolean;
  softHint?: boolean;
  showMoveComments?: boolean;
  mainlineMoveNumbers?: number[];
  headComment?: string;
  showResult?: boolean;
  answer?: string;
  question?: BoardQuestion | GeneralQuestion | ResultPositionsQuestion;
  investigationTargetFen?: string | null;
  viewSolution?: boolean;
  investigationFixedLine?: boolean;
  previousProblemId?: number;
  previousMove?: {
    from: Square;
    to: Square;
  };
  premoveNags?: MoveNagEntry[];
  premoveBoardShapes?: MoveBoardShapeEntry[];
  openingContext?: OpeningProblemContext;
};

export type ProblemViewerDetails = ProblemDetails & {
  solutionMoves: string;
  userComment?: string;
  gameResult?: Exclude<GameAnalysisResult, "*">;
};

export type UserInfo = {
  role: UserRoleEnum;
  rating: number;
  lifeRating: number;
};
